import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  useEffect(() => {
    const checkAuth = () => {
      // First check if admin is logged in (should block admin from user routes)
      const adminToken = localStorage.getItem('adminToken');
      const adminUser = localStorage.getItem('adminUser');
      
      if (adminToken && adminUser) {
        try {
          const admin = JSON.parse(adminUser);
          if (admin.adminRole) {
            // console.log('🚫 Admin user detected, blocking access to user route');
            setIsAdmin(true);
            setIsAuthenticated(false);
            return;
          }
        } catch (error) {
          // Invalid admin data, continue with regular user check
        }
      }

      // Check regular user authentication
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      
      if (token && user) {
        try {
          // Additional check: decode token to ensure it's not an admin token
          const payload = JSON.parse(atob(token.split('.')[1]));
          
          if (payload.adminRole) {
            // console.log('🚫 Admin token detected in user context, blocking access');
            setIsAdmin(true);
            setIsAuthenticated(false);
            return;
          }
          
          // console.log('✅ Regular user authenticated');
          setIsAuthenticated(true);
          setIsAdmin(false);
        } catch (error) {
          console.log('❌ Invalid token format');
          setIsAuthenticated(false);
          setIsAdmin(false);
        }
      } else {
        console.log('❌ No user token found');
        setIsAuthenticated(false);
        setIsAdmin(false);
      }
    };

    checkAuth();
  }, []);

  // Show loading while checking
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect admin users to admin dashboard
  if (isAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Render children if authenticated as regular user
  return <>{children}</>;
};

export default ProtectedRoute;